import { createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { simpleConfirm } from 'react-simple-dialogs'
import { toast } from 'react-toastify'
import ActionMenu from '../components/ActionMenu'
import OpenMap from '../components/OpenMaps'
import PhoneContact from '../components/PhoneContact'
import StatusBadge from '../components/StatusBadge'
import CardRow from '../components/card/CardRow'
import Table from '../components/dashboard/Table'
import DriverOnboardingModal from '../components/onboarding/DriverOnboardingModal'
import AcceptTagModal from '../components/tag/AcceptTagModal'
import { useNotificationContext } from '../contexts/NotificationContext'
import { useTagContext } from '../contexts/TagContext'
import { useUser } from '../contexts/UserContext'
import { getRelatedContractorName } from '../services/tagService'
import type { Tag } from '../types/tag'
import type { TagCardProps, TagStatus } from '../types/tag'
import type { User } from '../types/user'
import { formatDateTime } from '../utils/dateUtils'

type DisplayableKeys<T> = keyof T

const displayFields: {
  label: string
  valueKey: keyof Tag
}[] = [
  { label: 'Tag ID', valueKey: 'label' },
  { label: 'Dispatcher', valueKey: 'created_by' },
  { label: 'Contractor', valueKey: 'project' },
  { label: 'Material', valueKey: 'material' },
  { label: 'Pick up location', valueKey: 'origin_address' },
  { label: 'Drop off location', valueKey: 'destination_address' },
  { label: 'Notes', valueKey: 'notes' },
]

const getButtonConfig = (status: TagStatus) => {
  const upperStatus = status.toUpperCase()
  switch (upperStatus) {
    case 'NEW':
      return {
        text: 'Accept Dispatch',
        className: TagsDashboardStyles.acceptDispatchBtn,
      }
    case 'ACCEPTED':
      return {
        text: 'Start Dispatch',
        className: TagsDashboardStyles.startBtn,
      }
    case 'IN_PROGRESS':
      return {
        text: 'Continue',
        className: TagsDashboardStyles.continueBtn,
      }
    case 'SIGNED':
      return {
        text: 'Fill Additional Info',
        className: TagsDashboardStyles.viewDetailsBtn,
      }
    case 'COMPLETE':
      return {
        text: 'Review Tag Details',
        className: TagsDashboardStyles.reviewTagBtn,
      }
    case 'DECLINED':
      return null
    default:
      return {
        text: 'Start',
        className: TagsDashboardStyles.acceptDispatchBtn,
      }
  }
}

const formatPayRate = (rate: number, type: string | null): string => {
  return `$${rate.toFixed(2)}${
    type ? ` / ${type === 'HOURLY' ? 'hr' : 'project'}` : ''
  }`
}

const TagCard: React.FC<TagCardProps & { onDecline: (tag: Tag) => void }> = ({
  tag,
  onFillOut,
  onDecline,
}) => {
  const buttonConfig = getButtonConfig(tag.status)
  const [contractorName, setContractorName] = useState<string>('-')

  useEffect(() => {
    const fetchContractorName = async () => {
      const name = await getRelatedContractorName(tag)
      setContractorName(name)
    }
    fetchContractorName()
  }, [tag])

  // Extract date and time from start_date
  const { date: startDate, time: startTime } = formatDateTime(tag.start_date)

  const formatValue = (
    key: DisplayableKeys<Tag>,
    value: unknown,
    type: string | null,
    isTimeColumn?: boolean,
  ): React.ReactNode => {
    if (!value) return '-'

    // Format dates
    if (key === 'created_date') {
      const date = new Date(value as string)
      if (Number.isNaN(date.getTime())) return 'Invalid Date'

      if (isTimeColumn) {
        // Format time as HH:MM AM/PM
        return date.toLocaleString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      } else {
        // Format date as MM/DD/YYYY
        return date.toLocaleString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        })
      }
    }

    if (key === 'rate_type') {
      return formatPayRate(Number(value), type)
    }
    if (key === 'label') {
      return value
    }
    if (key === 'created_by' && typeof value === 'object') {
      const createdBy = value as {
        first_name?: string
        last_name?: string
        phone_number?: string
      }
      return (
        <PhoneContact
          displayName={
            `${createdBy.first_name || ''} ${createdBy.last_name || ''}`.trim() ||
            '-'
          }
          phoneNumber={createdBy.phone_number}
        />
      )
    }
    if (key === 'project' && typeof value === 'object') {
      return contractorName
    }
    if (key === 'material') {
      return String(value || '-')
    }
    if (key === 'origin_address' || key === 'destination_address') {
      return <OpenMap address={String(value)} />
    }

    return String(value || '')
  }

  return (
    <div className={css(TagsDashboardStyles.card)}>
      <div className={css(TagsDashboardStyles.cardHeader)}>
        <h2 className={css(TagsDashboardStyles.cardTitle)}>Dispatch</h2>
        <StatusBadge status={tag.status} />
      </div>
      <div className={css(TagsDashboardStyles.cardBody)}>
        <CardRow label="TAG ID" value={tag.label} />
        <CardRow
          label="DISPATCHER"
          value={
            <PhoneContact
              displayName={
                `${tag.created_by.first_name || ''} ${tag.created_by.last_name || ''}`.trim() ||
                '-'
              }
              phoneNumber={tag.created_by.phone_number}
            />
          }
        />
        <CardRow label="CONTRACTOR" value={contractorName} />
        <CardRow label="START DATE" value={startDate} />
        <CardRow label="START TIME" value={startTime} />
        <CardRow label="MATERIAL" value={tag.material || '-'} />
        <CardRow
          label="PICK UP LOCATION"
          value={<OpenMap address={tag.origin_address || ''} />}
        />
        <CardRow
          label="DROP OFF LOCATION"
          value={<OpenMap address={tag.destination_address || ''} />}
        />
        {tag.notes && <CardRow label="NOTES" value={tag.notes} vertical />}
      </div>
      <div className={css(TagsDashboardStyles.actionColumn)}>
        {buttonConfig && (
          <button
            className={css(
              TagsDashboardStyles.fillOutBtn,
              buttonConfig.className,
            )}
            onClick={() => onFillOut(String(tag.id))}
          >
            <i
              className={`fa-solid fa-file-plus ${css(
                TagsDashboardStyles.fillOutIcon,
              )}`}
            />
            {buttonConfig.text}
          </button>
        )}
        {tag.status.toUpperCase() === 'NEW' && (
          <button
            className={css(TagsDashboardStyles.rejectBtn)}
            onClick={() => onDecline(tag)}
          >
            Decline Dispatch
          </button>
        )}
      </div>
    </div>
  )
}

const DriverDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { tags, isLoading, error, isOffline, updateTagStatus } = useTagContext()
  const { user } = useUser()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)
  const location = useLocation()
  const [onboardingOpen, setOnboardingOpen] = useState(false)
  const [acceptingTag, setAcceptingTag] = useState<Tag | null>(null)

  // Use the centralized notification context
  const { tags: tagNotifications } = useNotificationContext()

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Auto-open driver onboarding modal if user hasn't completed onboarding
  useEffect(() => {
    const isDriver = user?.user_type === 'DRIVER'

    if (user && !user.has_completed_driver_onboarding && isDriver) {
      setOnboardingOpen(true)
    }
  }, [user])

  const handleFillOut = async (tagId: string) => {
    try {
      const tag = tags.find((t) => String(t.id) === tagId)

      if (!tag) {
        console.error('No tag found for id:', tagId)
        return
      }

      const status = tag.status.toUpperCase()

      switch (status) {
        case 'NEW':
          setAcceptingTag(tag)
          break

        case 'ACCEPTED':
          if (
            await simpleConfirm({
              title: 'Start Dispatch',
              message: 'Are you ready to start this Dispatch?',
              confirmLabel: 'Start Dispatch',
              cancelLabel: 'Cancel',
            })
          ) {
            await updateTagStatus(tagId, 'IN_PROGRESS')
            navigate(`/tags/open/${tagId}/1/details`)
          }
          break

        case 'IN_PROGRESS':
          navigate(`/tags/open/${tagId}/loads`)
          break

        case 'SIGNED':
          navigate(`/tags/open/${tagId}/expenses`)
          break

        case 'COMPLETE':
          navigate(`/tags/completed/${tagId}/confirmation`)
          break
      }
    } catch (error) {
      console.error('Error in handleFillOut:', error)
    }
  }

  const handleDecline = (tag: Tag) => {
    setAcceptingTag(tag)
    // The modal will handle decline mode via initial state
  }

  const columnHelper = createColumnHelper<Tag>()

  const columns = [
    columnHelper.display({
      id: 'action',
      cell: (props) => {
        const buttonConfig = getButtonConfig(props.row.original.status)
        const actions = [] as {
          label: string
          icon?: string
          onClick: () => void
          className?: string
          disabled?: boolean
        }[]

        if (buttonConfig) {
          actions.push({
            label: buttonConfig.text,
            icon: 'fa-solid fa-file-plus',
            onClick: () => handleFillOut(String(props.row.original.id)),
            className: css(
              TagsDashboardStyles.fillOutBtn,
              buttonConfig.className,
            ),
          })
        }

        if (props.row.original.status.toUpperCase() === 'NEW') {
          actions.push({
            label: 'Decline Dispatch',
            icon: 'fa-solid fa-times',
            onClick: () => {
              const tag = props.row.original
              handleDecline(tag)
            },
            className: css(TagsDashboardStyles.rejectBtn),
          })
        }

        return <ActionMenu actions={actions} />
      },
      header: () => <span>Actions</span>,
    }),
    columnHelper.accessor('label', {
      cell: (info) => info.getValue(),
      header: () => <span>Tag ID</span>,
    }),
    columnHelper.accessor('start_date', {
      cell: (info) => formatDateTime(info.getValue()).date,
      header: () => <span>Start Date</span>,
    }),
    columnHelper.accessor('start_date', {
      cell: (info) => {
        const { time } = formatDateTime(info.getValue())
        return time
      },
      header: () => <span>Start Time</span>,
    }),
    columnHelper.accessor('created_by', {
      cell: (info) => {
        const createdBy = info.getValue()
        return typeof createdBy === 'object' ? (
          <PhoneContact
            displayName={
              `${createdBy.first_name || ''} ${createdBy.last_name || ''}`.trim() ||
              '-'
            }
            phoneNumber={createdBy.phone_number}
          />
        ) : (
          createdBy
        )
      },
      header: () => <span>Dispatcher</span>,
    }),
    columnHelper.accessor('status', {
      cell: (info) => <StatusBadge status={info.getValue()} />,
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor('material', {
      cell: (info) => {
        const material = info.getValue()
        return typeof material === 'object'
          ? JSON.stringify(material)
          : material
      },
      header: () => <span>Material</span>,
    }),
    columnHelper.accessor('origin_address', {
      cell: (info) => <OpenMap address={info.getValue() || ''} />,
      header: () => <span>Pick up location</span>,
    }),
    columnHelper.accessor('destination_address', {
      cell: (info) => <OpenMap address={info.getValue() || ''} />,
      header: () => <span>Drop off location</span>,
    }),
  ]

  const filteredTagsByStatus = tags.filter((tag) => {
    const isCompletedRoute = location.pathname.includes('/tags/completed')
    const status = tag.status.toUpperCase()

    if (isCompletedRoute) {
      return status === 'COMPLETE'
    } else {
      return status !== 'COMPLETE'
    }
  })

  const filteredTags = filteredTagsByStatus.filter((tag) => {
    return (
      String(tag.id).includes(searchTerm) ||
      tag.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${tag.created_by?.first_name} ${tag.created_by?.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    )
  })

  return (
    <div className={css(TagsDashboardStyles.tagsDashboard)}>
      <div className={css(TagsDashboardStyles.mainContent)}>
        <DriverOnboardingModal
          open={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
        />
        {isOffline && (
          <div className={css(TagsDashboardStyles.offlineIndicator)}>
            You are offline. Changes will sync when you're back online.
          </div>
        )}
        {isLoading && (
          <div className={css(TagsDashboardStyles.loadingContainer)}>
            <i className="fa-duotone fa-solid fa-spinner-third fa-spin fa-3x" />
            <p className={css(TagsDashboardStyles.loadingText)}>
              Loading tags...
            </p>
          </div>
        )}
        <div className={css(TagsDashboardStyles.header)}>
          <h2
            className={css(
              TagsDashboardStyles.title,
              TagsDashboardStyles.dispatchTitle,
            )}
          >
            Dispatch
          </h2>
        </div>

        <div className={css(TagsDashboardStyles.desktopView)}>
          <Table data={filteredTags} columns={columns} />
        </div>

        <div className={css(TagsDashboardStyles.mobileView)}>
          {filteredTags.map((tag) => (
            <TagCard
              key={tag.id}
              tag={tag}
              onFillOut={handleFillOut}
              onDecline={handleDecline}
            />
          ))}
        </div>
        {acceptingTag && (
          <AcceptTagModal
            tag={acceptingTag}
            onClose={() => setAcceptingTag(null)}
            onSuccess={async () => {
              setAcceptingTag(null)
              // Mark tag as read when accepted
              try {
                await tagNotifications.markAsRead(Number(acceptingTag.id))
              } catch (error) {
                console.error('Failed to mark tag as read:', error)
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

const TagsDashboardStyles = StyleSheet.create({
  tagsDashboard: {
    display: 'flex',
    padding: '16px',
    '@media (max-width: 1024px)': {
      padding: '12px',
    },
  },
  mainContent: {
    flex: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: 20,
    '@media only screen and (max-width:1023px)': {
      display: 'none',
    },
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '16px',
      padding: '12px',
    },
  },
  title: {
    margin: 0,
    '@media (max-width: 1024px)': {
      fontSize: '1.5rem',
    },
  },
  desktopView: {
    '@media (max-width: 1024px)': {
      display: 'none',
    },
  },
  mobileView: {
    display: 'none',
    '@media (max-width: 1024px)': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #E5E7EB',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px',
  },
  tagId: {
    fontWeight: 600,
    color: '#111827',
    fontSize: '12px',
  },
  actionColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: 16,
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  fillOutBtn: {
    padding: '10px 20px',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 8,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minWidth: '160px',
    whiteSpace: 'nowrap',
  },
  rejectBtn: {
    padding: '10px 20px',
    backgroundColor: 'transparent',
    color: '#EF4444',
    border: 'none',
    cursor: 'pointer',
    borderRadius: 8,
    fontWeight: 500,
    width: '100%',
    textAlign: 'center',
    marginLeft: '7px',
    fontSize: '14px',
  },
  acceptDispatchBtn: {
    backgroundColor: '#10B981',
  },
  startBtn: {
    color: '#4B5563',
    background: '#FDE68A',
  },
  continueBtn: {
    backgroundColor: '#10B981',
  },
  viewDetailsBtn: {
    color: '#E1EFFE',
    background: '#1C64F2',
  },
  reviewTagBtn: {
    color: '#FFFFFF',
    background: '#7C3AED',
  },
  fillOutIcon: {
    marginRight: 8,
  },
  syncIndicator: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    padding: '8px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    borderRadius: '4px',
    fontSize: '14px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#6B7280',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
  },
  offlineIndicator: {
    position: 'fixed',
    top: '16px',
    right: '16px',
    padding: '8px 16px',
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    borderRadius: '8px',
    fontSize: '14px',
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    border: '1px solid #FCD34D',
    ':before': {
      content: '""',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#92400E',
    },
  },

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },

  notesSection: {
    marginTop: '16px',
  },
  notesLabel: {
    fontSize: '14px',
    color: '#6B7280',
    marginBottom: '4px',
  },
  notesField: {
    padding: '12px',
    backgroundColor: '#F9FAFB',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
    fontSize: '14px',
    color: '#111827',
    minHeight: '200px',
  },
  cardTitle: {
    margin: 0,
    textAlign: 'left',
    fontSize: '1.2rem',
    fontWeight: 600,
    color: '#111827',
  },
  dispatchTitle: {
    fontSize: '1.4rem',
    fontWeight: 600,
  },
})

export default DriverDashboard
